import { useCallback, useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetPinnedViews,
  getGetPinnedViewsQueryKey,
  useCreatePinnedView,
  useUpdatePinnedView,
  useDeletePinnedView,
  useReorderPinnedViews,
  useGetFolders,
  getGetFoldersQueryKey,
  useGetDocs,
  getGetDocsQueryKey,
  type PinnedView,
} from "@workspace/api-client-react";
import {
  pinnablePath, resolveRouteLabel, resolveRouteIcon, tabLabel, isSafeAppPath,
  type RouteLabelContext, type RouteIcon,
} from "@/lib/pinnedViews";
import { useToast } from "@/hooks/use-toast";

export type PinnedTab = PinnedView & {
  // What to render on the tab: the user's own name if they renamed it,
  // otherwise the label re-derived from the route right now (see
  // resolveRouteLabel). Never empty.
  displayLabel: string;
  // False once the route's target is gone (a deleted list, a doc that was
  // removed) — the tab still navigates, since the destination may come back
  // and closing it is the user's call, not ours.
  isResolvable: boolean;
  // Which icon the tab wears — the same one the nav gives that screen. An
  // identity, not a component: see resolveRouteIcon.
  icon: RouteIcon;
};

// Everything the bottom tab bar and the pin control need, in one place: the
// tabs with their live labels, whether the screen you're on is pinned, and
// the four mutations. Folders and docs come from queries the nav already
// holds, so the labels cost no extra request.
export function usePinnedViews() {
  const [location] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: pins } = useGetPinnedViews({ query: { queryKey: getGetPinnedViewsQueryKey() } });

  // This hook runs on every screen (it's mounted in Layout), so the two
  // label-source queries are only enabled when a name actually depends on
  // them. Without this, opening Settings would fetch the whole folder list —
  // which aggregates over messages_cache — purely to label tabs that are all
  // statically named anyway. Inside the inbox both queries are already in
  // flight, so React Query dedupes and this costs nothing there.
  const paths = useMemo(() => {
    const current = pinnablePath(location, search);
    return [current, ...(pins ?? []).map((p) => p.path)];
  }, [location, search, pins]);
  const needsFolders = paths.some((p) => /^\/(list|folder|slack|teams|chat)\//.test(p));
  const needsDocs = paths.some((p) => /^\/docs\/\d/.test(p));

  const { data: folders } = useGetFolders({ query: { queryKey: getGetFoldersQueryKey(), enabled: needsFolders } });
  const { data: docs } = useGetDocs({ query: { queryKey: getGetDocsQueryKey(), enabled: needsDocs } });

  const labelContext: RouteLabelContext = useMemo(() => ({
    folders: folders ?? [],
    docs: (docs ?? []).map((d) => ({ id: d.id, title: d.title })),
  }), [folders, docs]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: getGetPinnedViewsQueryKey() });
  }, [queryClient]);

  const createPin = useCreatePinnedView();
  const updatePin = useUpdatePinnedView();
  const deletePin = useDeletePinnedView();
  const reorderPins = useReorderPinnedViews();

  // The pinnable identity of the screen you're on right now — transient
  // ?message=/?task= selections stripped, so pinning twice from the same
  // screen is the same pin. (paths[0] above is this same value.)
  const currentPath = paths[0]!;

  const tabs: PinnedTab[] = useMemo(() => {
    // A row whose path fails validation is dropped rather than rendered:
    // the server rejects these on write, so one can only appear via a row
    // written before that check existed or a direct DB edit — either way it
    // is not something to hand to the router.
    return (pins ?? [])
      .filter((pin) => isSafeAppPath(pin.path))
      .map((pin) => ({
        ...pin,
        displayLabel: tabLabel(pin, labelContext),
        isResolvable: resolveRouteLabel(pin.path, labelContext) !== null,
        icon: resolveRouteIcon(pin.path, labelContext),
      }));
  }, [pins, labelContext]);

  const currentTab = useMemo(
    () => tabs.find((tab) => tab.path === currentPath) ?? null,
    [tabs, currentPath],
  );

  // Whether *this* screen can be pinned at all. Unresolvable routes are the
  // interesting case: a screen this resolver has never heard of would pin as
  // a nameless tab, which is worse than no pin — so the control disables
  // itself and says why, rather than silently producing a bad tab.
  const canPinCurrent = useMemo(
    () => isSafeAppPath(currentPath) && resolveRouteLabel(currentPath, labelContext) !== null,
    [currentPath, labelContext],
  );

  const togglePinCurrent = useCallback(() => {
    if (currentTab) {
      deletePin.mutate({ id: currentTab.id }, {
        onSuccess: invalidate,
        onError: () => toast({ title: "Couldn't unpin this view", variant: "destructive" }),
      });
      return;
    }

    const label = resolveRouteLabel(currentPath, labelContext);
    if (!label) {
      toast({ title: "This screen can't be pinned yet", variant: "destructive" });
      return;
    }

    createPin.mutate({ data: { path: currentPath, label } }, {
      onSuccess: invalidate,
      onError: () => toast({ title: "Couldn't pin this view", variant: "destructive" }),
    });
  }, [currentTab, currentPath, labelContext, createPin, deletePin, invalidate, toast]);

  const closeTab = useCallback((id: number) => {
    deletePin.mutate({ id }, {
      onSuccess: invalidate,
      onError: () => toast({ title: "Couldn't close this tab", variant: "destructive" }),
    });
  }, [deletePin, invalidate, toast]);

  // A null label clears the user's own name and hands the tab back to
  // auto-naming, which is how a rename is undone.
  const renameTab = useCallback((id: number, label: string | null) => {
    updatePin.mutate({ id, data: { label } }, {
      onSuccess: invalidate,
      onError: () => toast({ title: "Couldn't rename this tab", variant: "destructive" }),
    });
  }, [updatePin, invalidate, toast]);

  const reorderTabs = useCallback((ids: number[]) => {
    // The server requires the order to name every pin exactly once, and
    // `tabs` can be a subset (an unrenderable row was filtered out above) —
    // so any pin the bar didn't show keeps its place at the end rather than
    // failing the whole reorder.
    const hidden = (pins ?? []).map((p) => p.id).filter((id) => !ids.includes(id));
    reorderPins.mutate({ data: { ids: [...ids, ...hidden] } }, {
      onSuccess: invalidate,
      // The bar has already moved locally by this point (framer-motion's
      // Reorder is optimistic), so a failure has to put it back rather than
      // leave the screen disagreeing with the server.
      onError: () => {
        invalidate();
        toast({ title: "Couldn't save the new tab order", variant: "destructive" });
      },
    });
  }, [pins, reorderPins, invalidate, toast]);

  return {
    tabs,
    currentPath,
    isCurrentPinned: currentTab !== null,
    canPinCurrent,
    togglePinCurrent,
    closeTab,
    renameTab,
    reorderTabs,
  };
}
